import axios from "axios";
import { nanoid } from "nanoid/non-secure";
import { firebaseFunctionsUrl } from "../Helpers/FirebaseEnvironments";

const extractJson = (content: string) => {
  try {
    // Match the JSON part inside the backticks
    const jsonMatch = content.startsWith("```")
      ? content.match(/```json([\s\S]*?)```/)?.[1]
      : content;

    if (jsonMatch) {
      // Parse the extracted JSON string
      const jsonData = JSON.parse(jsonMatch);
      if (jsonData.hasOwnProperty("elements")) {
        return jsonData.elements;
      } else {
        return jsonData;
      }
    } else {
      console.error("No JSON found in the content.");
      return null;
    }
  } catch (error) {
    // console.error("Error parsing JSON:", error, content);
    return null;
  }
};

function transformMindMapData(
  inputData: { nodes: any[]; edges: any[] },
  newFlowPosition: { x: number; y: number }
) {
  try {
    let no = 0;
    let rootId = "5f2a2d57-6be4-4deb-a4a9-502ab4f5d209";
    const transformedNodes = inputData.nodes.map((node) => {
      no++;
      if (no === 1) {
        rootId = node.id;
      }
      const transformedNode = {
        id: no === 1 ? rootId : node.id,
        type: no === 1 ? "center" : "mindmap",
        data: {
          label: node.data.label,
          description: node.data.description,
          flagged: "none",
          notes: node.data.notes,
          complete: false,
          color: "black",
          backgroundColor: "#ffffff",
          presentationOrder: "",
          value: "0",
          values: [],
          rollupValues: [],
          customProperties: [],
          priority: "",
          links: [],
        },
        style: {
          backgroundColor: "#fff",
        },
        position:
          no === 1
            ? newFlowPosition
            : node.position
            ? node.position
            : { x: 0, y: 0 },
        positionAbsolute:
          no === 1
            ? newFlowPosition
            : node.position
            ? node.position
            : { x: 0, y: 0 },
        width: node.width,
        height: 18,
        dragHandle: ".dragHandle",
        parentId: no === 1 ? "" : node.parentId,
      };

      return transformedNode;
    });

    const transformedEdges = inputData.edges.map((edge) => ({
      id: edge.id,
      source: edge.source === "1" ? rootId : edge.source,
      target: edge.target,
      type: edge.source === "1" ? "center" : "mindmap",
    }));

    transformedNodes.forEach((node) => {
      if (node.id !== rootId) {
        const parentEdge = transformedEdges.find(
          (edge) => edge.target === node.id
        );
        if (parentEdge) {
          node.parentId = parentEdge.source;
        }
      }
    });
    return { nodes: transformedNodes, edges: transformedEdges };
  } catch (error) {
    console.error("Error transforming mind map data:", error);
    return { nodes: [], edges: [] };
  }
}

export const generateAiMap = async (
  topic: string,
  depth: number,
  setLoading: (loading: boolean) => void,
  nodes: any[],
  edges: any[],
  onSetNodes: (nodes: any[]) => void,
  onSetEdges: (edges: any[]) => void,
  showAlert: (title: string, message: string) => void,
  newFlowPosition: { x: number; y: number }
) => {
  try {
    if (topic.trim()) {
      setLoading(true);
      axios.defaults.withCredentials = true;
      const res = await axios.post(firebaseFunctionsUrl("openai"), {
        query: { topic: topic, levels: depth },
      });
      //console.log("Response from AI assistant:", res);
      const completionContent = res.data.choices[0].message.content;

      if (completionContent) {
        // console.log("Returned Data:", completionContent);
        // Remove preceding or closing ` signs before the JSON data
        //const cleanedContent = completionContent.replace(/^[`]+|[`]+$/g, '');
        const data = extractJson(completionContent);
        if (data) {
          // console.info("Extracted JSON data:", data);
          const transformedNodes = transformMindMapData(data, newFlowPosition);

          const idMap = new Map<string, string>();

          transformedNodes.nodes.forEach((node) => {
            const newId = nanoid();
            idMap.set(node.id, newId);
            node.id = newId;
          });
          transformedNodes.nodes.forEach((node) => {
            if (node.parentId) {
              node.parentId = idMap.get(node.parentId); // || node.parentId;
            }
          });

          transformedNodes.edges.forEach((edge) => {
            edge.source = idMap.get(edge.source); // || edge.source;
            edge.target = idMap.get(edge.target); // || edge.target;
          });

          // console.log(
          //   "Extracted and transformed nodes:",
          //   transformedNodes.nodes,
          //   transformedNodes.edges
          // );

          const updatedNodes = [...nodes, ...transformedNodes.nodes];
          const updatedEdges = [...edges, ...transformedNodes.edges];

          onSetNodes(updatedNodes || []);
          onSetEdges(updatedEdges || []);

          //setTopic("");
        } else {
          console.error("Failed to extract and parse JSON.");
          showAlert(
            "Error",
            "The AI assistant couldn't find valid data at this point."
          );
        }
      } else {
        console.error("No completion content found.");
      }
    } else {
      showAlert("Notice", "Please enter a topic before generating a map.");
    }
  } catch (error) {
    console.error("Error generating map: ", error);
    showAlert(
      "Error",
      "An error occurred while generating the map. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

export const generateAiDocument = async (topic: string) => {
  try {
    if (topic.trim()) {
      axios.defaults.withCredentials = true;
      const res = await axios.post(firebaseFunctionsUrl("openaidoc"), {
        query: { topic: topic },
      });
      const completionContent = res.data.choices[0].message.content;

      if (completionContent) {
        return completionContent;
      } else {
        return "No completion content found.";
      }
    } else {
      return "Please enter a topic before generating output.";
    }
  } catch (error) {
    return "Error generating output: ";
  } finally {
  }
};
